import { useEffect, useMemo, useState } from 'react';
import {
  Box, Typography, Alert, Stack, Chip, CircularProgress, Paper,
  FormControlLabel, Switch, Link,
} from '@mui/material';
import ReleaseRow from '../releases/ReleaseRow';
import { readPlan, formatDate, todayKst } from '../../utils/releaseView';

// FEAT-023 — 제품 상세 「릴리즈」 탭.
//
// 🔴 계획과 이력은 입자가 다르다. 억지로 맞추지 않는다.
//   계획 = 제품 버전(마일스톤) — 2026-09-23 실측 24개 중 20개가 쓰고 있다.
//   이력 = SW 버전(릴리즈)   — 43개 저장소 344건이 실제 배포의 기록이다.
//   제품 버전과 SW 릴리즈를 잇는 근거(BOM)가 아직 없어서, 묶으면 없는 데이터를
//   지어내야 한다. 그래서 오늘을 기준선으로 위아래에 나란히 둔다.
//
// 데이터: GET /api/releases?product=<id>

export default function ProductReleasesTab({ productId }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [hint, setHint] = useState(null);
  const [showPre, setShowPre] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setData(null); setErr(null);
    fetch(`/api/releases?product=${encodeURIComponent(productId)}`)
      .then(async (r) => {
        const body = await r.json().catch(() => ({}));
        if (!r.ok) throw Object.assign(new Error(body.error || `HTTP ${r.status}`), { hint: body.hint });
        return body;
      })
      .then((d) => { if (!cancelled) setData(d); })
      .catch((e) => { if (!cancelled) { setErr(e.message); setHint(e.hint || null); } });
    return () => { cancelled = true; };
  }, [productId]);

  const product = useMemo(
    () => (data?.products || []).find((p) => p.id === productId) || null,
    [data, productId],
  );
  const plan = useMemo(() => readPlan(product), [product]);
  const releases = useMemo(
    () => (data?.releases || []).filter((r) => showPre || !r.prerelease),
    [data, showPre],
  );

  if (err) {
    return (
      <Alert severity="warning" sx={{ mt: 2 }}>
        {err}
        {hint && <Box sx={{ mt: 0.5, fontSize: 13, opacity: 0.85 }}>{hint}</Box>}
      </Alert>
    );
  }
  if (!data) {
    return (
      <Box sx={{ py: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
        <CircularProgress size={18} /> <Typography variant="body2">릴리즈 불러오는 중…</Typography>
      </Box>
    );
  }

  const preCount = (data.releases || []).filter((r) => r.prerelease).length;

  return (
    <Box sx={{ mt: 2 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          수집 {data.generated_at ? new Date(data.generated_at).toLocaleString('ko-KR') : '-'}
          {product?.sw_count ? ` · 구성 SW ${product.sw_count}개` : ''}
          {product?.sw_source === 'manual' && ' (수동 매핑)'}
          {' · '}
          <Link href="/releases" underline="hover">전사 릴리즈 보기</Link>
        </Typography>
        {preCount > 0 && (
          <FormControlLabel
            control={<Switch size="small" checked={showPre} onChange={(e) => setShowPre(e.target.checked)} />}
            label={<Typography variant="body2">사전 배포 포함 ({preCount})</Typography>}
          />
        )}
      </Stack>

      {/* 계획 — 제품 버전(마일스톤) */}
      <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 0.5 }}>계획</Typography>
      <Paper variant="outlined" sx={{ p: 1.5, mb: 1 }}>
        {plan ? (
          <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap" useFlexGap>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>제품 {plan.tag}</Typography>
            <Chip
              size="small"
              label={plan.state}
              color={plan.open ? 'primary' : 'default'}
              variant={plan.open ? 'filled' : 'outlined'}
              sx={{ height: 20, fontSize: 11 }}
            />
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              목표일 {plan.due ? formatDate(plan.due) : '미정'}
            </Typography>
            {plan.overdue > 0 && (
              <Chip size="small" color="error" label={`${plan.overdue}일 초과`} sx={{ height: 20, fontSize: 11 }} />
            )}
          </Stack>
        ) : (
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            예정된 제품 버전이 없습니다 — 제품 저장소에 마일스톤을 만들면 여기에 나타납니다.
          </Typography>
        )}
      </Paper>

      {/* 오늘 기준선 */}
      <Stack direction="row" alignItems="center" spacing={1} sx={{ my: 1.5 }}>
        <Box sx={{ flex: 1, borderTop: 1, borderColor: 'divider' }} />
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>오늘 · {formatDate(todayKst())}</Typography>
        <Box sx={{ flex: 1, borderTop: 1, borderColor: 'divider' }} />
      </Stack>

      {/* 이력 — SW 버전(릴리즈) */}
      <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 0.5 }}>
        이력 {releases.length > 0 && <span style={{ fontWeight: 400 }}>({releases.length}건)</span>}
      </Typography>
      <Paper variant="outlined">
        {releases.length === 0 ? (
          <Typography variant="body2" sx={{ p: 3, color: 'text.secondary', textAlign: 'center' }}>
            {(data.releases || []).length === 0
              ? '이 제품의 구성 SW 에서 발행된 릴리즈가 없습니다.'
              : '사전 배포만 있습니다 — 위 토글로 확인하세요.'}
          </Typography>
        ) : (
          releases.slice(0, 200).map((r) => (
            <ReleaseRow key={`${r.repo}@${r.tag}`} release={r} showProduct={false} />
          ))
        )}
      </Paper>
    </Box>
  );
}
